// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	ReactElement,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

// context
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// types
import {
	Account,
	Act,
	AmountByLoan,
	CashOutTotal,
	Category,
	CategoryAllocation,
	Component,
	ComponentAllocation,
	Contract,
	Document,
	DocumentType,
	Entity,
	ErrorCause,
	ExchangeRateByDate,
	ExpenseType,
	Feedback,
	FilePayload,
	FinancierExt,
	Ifr,
	Invoice,
	LoadingStatus,
	Loan,
	LoanCashOut,
	Option,
	PaymentPayload,
	Transaction,
	TransactionMetadata,
	TransactionPayload,
	TransactionType,
	WithdrawalApplication,
} from '../../../@types';
import { financiersExt } from '../../../@types/unions';

// router
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Button, Grid, SelectChangeEvent, SxProps } from '@mui/material';

// fields
import { Amounts, AmountsProps } from '../../fields/Amounts/Amounts';
import { AutocompleteTags } from '../../fields/AutocompleteTags/AutocompleteTags';
import { Checkbox } from '../../fields/Checkbox/Checkbox';
import { CheckboxConfirmation } from '../../fields/CheckboxConfirmation/CheckboxConfirmation';
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';
import { DocsTraceability } from '../../ui/DocsTraceability/DocsTraceability';
import { Empty } from '../../ui/Empty/Empty';
import { IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { ListItemTextAccount } from '../../ui/ListItemTextAccount/ListItemTextAccount';
import { TableInfoTransaction } from '../../ui/TableInfoTransaction/TableInfoTransaction';

// utils
import { formatDate } from '../../../utils/dates';
import { toKebabCase } from '../../../utils/strings';
import { postOrGetTags } from '../../../utils/tags';

// theme
import { theme } from '../../../theme';

// utils
import { formatCurrency } from '../../../utils/currency';
import { getAllocation } from '../../../utils/allocations';
import { translateError } from '../../../utils/translations';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface ValuesProps {
	acts: string[];
	amounts: AmountByLoan[];
	borrowerReference: string;
	category: Category;
	comments: string;
	component: Component;
	contract: string;
	currency: string;
	designatedAccount: string;
	date: string;
	expenseType: ExpenseType;
	fromAccount: string;
	fromEntity: string;
	invoice: string;
	invoiceAmount: string;
	invoiceCurrency: string;
	isDirect: boolean;
	tags: string[];
	toEntity: string;
	total: number;
	vendor: string;
}

export interface FormAddTransactionProps {
	hasBanner?: boolean;
	hasInvoiceMetadata?: boolean;
	onClose?: () => void;
	onFeedback?: (feedback: AlertFeedbackProps | null) => void;
	onSuccess?: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
	status: LoadingStatus;
	type?: TransactionType;
}

export const FormAddTransaction = ({
	hasBanner = false,
	hasInvoiceMetadata = false,
	onClose,
	onFeedback,
	onSuccess,
	setStatus,
	status,
	type = 'PAYMENT',
}: FormAddTransactionProps) => {
	// hooks
	const { idBeneficiary, idEntity, idSupplier } = useParams();
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogAct = searchParams.get('dialogAct');
	const paramDialogBeneficiaryId = searchParams.get('dialogBeneficiary');
	const paramDialogContract = searchParams.get('dialogContract');
	const paramDialogInvoice = searchParams.get('dialogInvoice');
	const paramDialogToEntity = searchParams.get('dialogToEntity');
	const paramIsOnChain = searchParams.get('isOnChain');
	const paramDialogStatus = searchParams.get('dialogStatus');

	// params derived
	const isOnChainFlag = paramIsOnChain === 'true';

	// state
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [acts, setActs] = useState<Act[]>([]);
	const [beneficiaries, setBeneficiaries] = useState<Entity[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [designatedAccount, setDesignatedAccount] = useState<Account | null>(null);
	const [docTypesSelected, setDocTypesSelected] = useState<string[]>([]);
	const [documentsInvoice, setDocumentsInvoice] = useState<Document[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>([]);
	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [fromAccount, setFromAccount] = useState<Account | null>(null);
	const [ifrs, setIfrs] = useState<Ifr[]>([]);
	const [invoice, setInvoice] = useState<Invoice | null>(null);
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [suppliers, setSuppliers] = useState<Entity[]>([]);
	const [transaction, setTransaction] = useState<Transaction | null>(null);
	const [withdrawalApplication, setWithdrawalApplication] = useState<WithdrawalApplication | null>(
		null
	);
	const [loansCashOut, setLoansCashOut] = useState<LoanCashOut[]>([]);
	const [values, setValues] = useState<ValuesProps>({
		acts: paramDialogAct ? [paramDialogAct] : [],
		amounts: [],
		borrowerReference: '',
		category: {} as Category,
		comments: '',
		component: {} as Component,
		contract: paramDialogContract || '',
		currency: '',
		date: '',
		designatedAccount: '',
		expenseType: {} as ExpenseType,
		fromEntity: idEntity || '',
		fromAccount: '',
		invoice: paramDialogInvoice || '',
		invoiceAmount: '',
		invoiceCurrency: '',
		isDirect: type === 'DIRECT',
		tags: [],
		toEntity: idSupplier || idBeneficiary || paramDialogBeneficiaryId || paramDialogToEntity || '',
		total: 0,
		vendor: '',
	});

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// vars
	const amountTotal = values.amounts.reduce((sum, a) => sum + a.amount + (a.tax || 0), 0);
	const contract =
		contracts.find((contract) => String(contract.id) === values.contract) || undefined;
	const contractCategories = contract?.categories as number[];
	const contractComponents = contract?.components as number[];
	const constractExpenseTypes = contract?.expenseTypes as number[];
	const categorySelected = values.category as Category;
	const expenseTypeSelected = values.expenseType as ExpenseType;
	const docTypesForExpenseTypeSelected =
		(expenseTypeSelected?.documentTypes as DocumentType[]) || [];
	const docTypesRequired = docTypesForExpenseTypeSelected.filter((docType) => docType.isRequired);
	const formId = 'form-add-payment';

	// can only be turned on when documents and transactions have a one-to-many relationship
	// otherwise transactions associated to same invoice will overwrite each other's documents
	const hasInvoiceDocsIncluded = false;

	const isCashBenefit = type === 'CASH_BENEFIT';
	const isDirectOrPayment = ['DIRECT', 'PAYMENT'].includes(type);
	const isDirectInitiated = type === 'DIRECT' && paramDialogStatus === 'INITIATED';
	const isOpEx = type === 'OPEX';
	const isPayment = type === 'PAYMENT';
	const isWfa = user?.role === 'WFA';
	const isDateAfterConfirmation =
		(isPayment || isCashBenefit) &&
		values.date &&
		project?.dateConfirmationReceipt &&
		values.date > project?.dateConfirmationReceipt;
	const dateProjectConfirmation = project?.dateConfirmationReceipt
		? formatDate({ date: new Date(project?.dateConfirmationReceipt) })
		: null;
	const hasRequiredDocs = docTypesRequired.length > 0 && !isOpEx;
	const fromAccountLoans: Loan[] = fromAccount ? (fromAccount.loans as Loan[]) : [];
	const fromAccountLoansGovOth = fromAccountLoans.filter((loan) =>
		financiersExt.includes(loan.financier as FinancierExt)
	);
	const hasOnlyGovOthLoans = fromAccountLoansGovOth.length === fromAccountLoans.length;
	const hasDesignatedAccount =
		fromAccount?.type === 'LOCAL' &&
		Boolean(fromAccount?.loans?.length) &&
		!hasOnlyGovOthLoans &&
		!fromAccount?.isReimbursement;
	const fromEntitySelected = entities.find((e) => String(e.id) === values.fromEntity);
	const invoiceSelected =
		!hasInvoiceMetadata && values.invoice
			? invoice || invoices.find((inv) => String(inv.id) === values.invoice)
			: undefined;
	const invoiceAmount = invoiceSelected ? String(invoiceSelected.amount) : values.invoiceAmount;
	const invoiceCurrency = invoiceSelected ? invoiceSelected.currency : values.invoiceCurrency;
	const isInvoiceInFromAccountCurrency = invoiceCurrency === values.currency;

	let loansForPayments: Loan[] = loans;

	if (fromAccount) {
		loansForPayments = fromAccount.loans as Loan[];
	}

	let amountContractInInvoiceCurrency: number | string | undefined;
	let amountContractInFromAccountCurrency: string | undefined;
	let isAmountGreaterThanInvoice = false;
	let isContractCurrencyDifferent = false;
	let isInvoiceGreaterThanContract = false;

	if (contract && contract.amounts) {
		amountContractInInvoiceCurrency = t('na');
		amountContractInFromAccountCurrency = t('na');

		if (invoiceCurrency) {
			const contractAmountInInvoiceCurrency = contract.amounts.find(
				(amount) => amount.currency === invoiceCurrency
			)?.amount;

			if (contractAmountInInvoiceCurrency) {
				if (invoiceAmount) {
					isInvoiceGreaterThanContract = Number(invoiceAmount) > contractAmountInInvoiceCurrency;
				}

				amountContractInInvoiceCurrency = formatCurrency({
					currency: invoiceCurrency,
					value: contractAmountInInvoiceCurrency,
				});
			}
		}

		if (values.currency) {
			isContractCurrencyDifferent = !contract.amounts.some(
				(amount) => amount.currency === values.currency
			);

			const contractAmountInFromAccountCurrency = contract.amounts.find(
				(amount) => amount.currency === values.currency
			)?.amount;

			if (contractAmountInFromAccountCurrency) {
				amountContractInFromAccountCurrency = formatCurrency({
					currency: values.currency,
					value: contractAmountInFromAccountCurrency,
				});
			}
		}
	}

	if (invoiceAmount && isInvoiceInFromAccountCurrency) {
		isAmountGreaterThanInvoice = amountTotal > Number(invoiceAmount);
	}

	let toEntitySelected;

	if (isCashBenefit) {
		toEntitySelected = beneficiaries.find((b) => String(b.id) === values.toEntity);
	} else if (isOpEx) {
		toEntitySelected = entities.find((e) => String(e.id) === values.toEntity);
	} else if (isPayment) {
		toEntitySelected = suppliers.find((s) => String(s.id) === values.toEntity);
	}

	// check if from account has disabled loans
	let hasDisabledLoans = false;

	for (let i = 0; i < fromAccountLoans.length; i++) {
		if (fromAccountLoans[i]?.status === 'DISABLED') {
			hasDisabledLoans = true;
			break;
		}
	}

	// fetch acts
	const { isLoading: isLoadingActs } = useFetch({
		isEnabled: Boolean(isPayment && project?.id),
		url: `${process.env.REACT_APP_API_URL}/acts?project=${project?.id}&status=READY&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setActs(res.data);
			}
		},
	});

	// fetch contracts
	let urlFetchContracts = `${process.env.REACT_APP_API_URL}/contracts?fields=amounts,categories,components,expenseTypes,id,idStep,num,status&status=ACTIVE`;

	if (values.toEntity) {
		urlFetchContracts += `&entities=${values.toEntity}`;
	}

	const { fetchRequest: fetchContracts } = useFetch({
		isLazy: true,
		url: urlFetchContracts,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}
		},
	});

	// fetch invoice if paramDialogInvoice is present
	useFetch({
		isEnabled: Boolean(paramDialogInvoice),
		url: `${process.env.REACT_APP_API_V2_URL}/invoices/${paramDialogInvoice}?fields=amount,currency,id,num`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const invoiceNew = res.data[0];
				setInvoice(invoiceNew);

				// set currency using invoice
				setValues((valuesPrev) => ({
					...valuesPrev,
					currency: invoiceNew.currency || valuesPrev.currency,
					invoice: String(invoiceNew.id || valuesPrev.invoice),
				}));
			}
		},
	});

	// fetch invoices (lazy, filtered by selected contract)
	const urlFetchInvoices = `${process.env.REACT_APP_API_V2_URL}/invoices?contract=${values.contract}&fields=amount,currency,id,num&status=READY_FOR_PAYMENT`;

	const { fetchRequest: fetchInvoices } = useFetch({
		isLazy: true,
		url: urlFetchInvoices,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setInvoices(res.data);
			}
		},
	});

	// fetch categories
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&fields=id,idWbg,name&status=ACTIVE&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCategories(res.data);
			}
		},
	});

	// fetch components
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComponents(res.data);
			}
		},
	});

	// fetch expense types
	let urlFetchExpenseTypes = `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&populate=documentTypes&status=ACTIVE&orderBy=name&order=ASC`;

	if (userEntity) {
		urlFetchExpenseTypes += `&entity=null,${userEntity.id}`;
	}

	useFetch({
		isEnabled: Boolean(project?.id && user),
		url: urlFetchExpenseTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseTypes(res.data);
			}
		},
	});

	// fetch accounts
	let urlFetchAccounts = `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&fields=currency,id,institution,name,status,type&orderBy=name&order=ASC`;

	if (idEntity) {
		urlFetchAccounts += `&entity=${idEntity}`;
	}

	useFetch({
		isEnabled: Boolean((!isOpEx || (isOpEx && idEntity)) && project?.id),
		url: urlFetchAccounts,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setAccounts(res.data);
		},
	});

	// fetch loans
	const { isLoading: isLoadingLoans } = useFetch({
		isEnabled: values.isDirect,
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=code,financier,id,status`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch suppliers
	let urlFetchSuppliers = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name&type=SUPPLIER&orderBy=name&order=ASC`;

	if (values.acts.length) {
		urlFetchSuppliers += `&acts=${values.acts.join(',')}`;
	}

	const { fetchRequest: fetchSuppliers } = useFetch({
		isLazy: true,
		url: urlFetchSuppliers,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setSuppliers(res.data);
			}
		},
	});

	// fetch beneficiaries
	useFetch({
		isEnabled: Boolean(isCashBenefit && project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,subtype&type=BENEFICIARY&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiaries(res.data);
			}
		},
	});

	// fetch entities
	useFetch({
		isEnabled: Boolean(isOpEx && project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,wallet&type=BORROWER,INTERMEDIARY,PIU,SUB_PIU&populate=wallet&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch invoice documents
	const { fetchRequest: fetchInvoiceDocuments } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/documents?invoice=${values.invoice}&fields=blob,id,metadata,name,type,url&populate=type`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const documentsForInvoice = res.data as Document[];

				// set documents for invoice
				setDocumentsInvoice(documentsForInvoice);

				// add invoice doc types to docTypesSelected
				if (hasInvoiceDocsIncluded) {
					setDocTypesSelected((docTypesSelectedPrev) => {
						const docTypesForInvoice = documentsForInvoice
							.filter((doc) => doc.type)
							.map((doc) => String((doc.type as DocumentType).id));
						return [...docTypesSelectedPrev, ...docTypesForInvoice];
					});
				}
			}
		},
	});

	// options
	const optionsActs = acts.map((act) => {
		return {
			disabled: String(act.id) === paramDialogAct,
			label: act.name,
			MenuItemProps: {
				resource: act,
			},
			value: String(act.id),
		};
	});

	const optionsAccounts = accounts.map((account) => {
		return {
			disabled: account.status === 'DEACTIVATED',
			label: <ListItemTextAccount account={account} />,
			secondary: account.institution,
			value: String(account.id),
		};
	});

	const optionsAccountsDesignated = fromAccount
		? (fromAccount?.accounts as Account[])
				.filter((account) => {
					return account.type === 'DESIGNATED';
				})
				.map((account) => {
					return {
						disabled: account.status === 'DEACTIVATED',
						label: <ListItemTextAccount account={account} />,
						secondary: account.institution,
						value: String(account.id),
					};
				})
		: [];

	const optionsBeneficiaries = beneficiaries.map((beneficiary) => {
		return {
			label: beneficiary.name,
			value: String(beneficiary.id),
		};
	});

	let optionsDocTypes: Option[] = [];

	if (expenseTypeSelected) {
		optionsDocTypes = ((expenseTypeSelected?.documentTypes || []) as DocumentType[]).map(
			(docType) => {
				return {
					label: docType.name,
					value: String(docType.id),
				};
			}
		);
	}

	const optionsCategories = categories.map((category) => {
		return {
			disabled: contractCategories?.length ? !contractCategories.includes(category.id) : false,
			label: category.name,
			MenuItemProps: {
				resource: category,
			},
			value: String(category.id),
		};
	});

	const optionsContracts = (
		isDirectInitiated && paramDialogContract
			? contracts.filter((c) => String(c.id) === String(paramDialogContract))
			: contracts
	).map((contractOption) => {
		// Display comma-separated list of currencies
		const currencies =
			contractOption.amounts.length > 0
				? contractOption.amounts.map((amount) => amount.currency).join(', ')
				: undefined;

		return {
			label: contractOption.num,
			MenuItemProps: {
				resource: contractOption,
			},
			secondary: currencies,
			value: String(contractOption.id),
		};
	});

	const optionsComponents = components.map((component) => {
		return {
			disabled: contractComponents?.length ? !contractComponents.includes(component.id) : false,
			label: component.name,
			MenuItemProps: {
				resource: component,
			},
			value: String(component.id),
		};
	});

	const optionsInvoices = invoices.map((invoice) => {
		return {
			label: invoice.num,
			secondary: formatCurrency({
				currency: invoice.currency,
				value: invoice.amount,
			}),
			value: String(invoice.id),
		};
	});

	const optionsExpenseTypes = expenseTypes.map((expenseType) => {
		return {
			disabled: constractExpenseTypes?.length
				? !constractExpenseTypes?.includes(expenseType.id)
				: false,
			label: expenseType.name,
			MenuItemProps: {
				resource: expenseType,
			},
			value: String(expenseType.id),
		};
	});

	const optionsSuppliers = suppliers.map((supplier) => {
		return {
			label: supplier.name,
			value: String(supplier.id),
		};
	});

	const optionsEntities = entities.map((entity) => {
		return {
			disabled: entity.wallet?.status === 'INACTIVE',
			label: entity.name,
			value: String(entity.id),
		};
	});

	const handleChangeEntity = async (e: SelectChangeEvent<unknown>) => {
		const url = `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&entity=${e.target.value}&fields=id,currency,institution,loans,name,status,type&populate=loans`;
		const fetchEntityAccounts = await fetch(url, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		});

		const resEntityAccounts = await fetchEntityAccounts.json();

		// set entity
		setValues((prevState) => {
			return {
				...prevState,
				fromEntity: String(e.target.value),
			};
		});

		// reset account
		setValues((prevState) => {
			return {
				...prevState,
				fromAccount: '',
			};
		});

		// set accounts
		if (resEntityAccounts.data) {
			setAccounts(resEntityAccounts.data);
		}
	};

	const getExchangeRatesForCurrency = useCallback(
		(baseCurrency: string): ExchangeRateByDate[] => {
			let currenciesRequired: string[] = [];

			if (featureFlags['IFRS'] && project?.currencies) {
				project.currencies.forEach((projectCurrency) => {
					if (projectCurrency.currency !== baseCurrency) {
						currenciesRequired.push(projectCurrency.currency);
					}
				});
			}

			if (designatedAccount && designatedAccount.currency !== baseCurrency) {
				if (!currenciesRequired.includes(designatedAccount.currency)) {
					currenciesRequired.push(designatedAccount.currency);
				}
			}

			// Remove duplicates
			currenciesRequired = [...new Set(currenciesRequired)];

			// Create exchange rate entries for all required currencies
			return currenciesRequired.map((currency) => ({
				currency,
				dateExchangeRate: '',
				exchangeRate: 0,
			}));
		},
		[designatedAccount, project?.currencies]
	);

	const handleChangeFromAccount = async (e: SelectChangeEvent<unknown>) => {
		const url = `${process.env.REACT_APP_API_URL}/accounts/${e.target.value}?populate=accounts,loans`;
		const fetchAccount = await fetch(url, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		});

		const resAccount = await fetchAccount.json();

		const valuesNew: Partial<ValuesProps> = {
			designatedAccount: '',
			fromAccount: String(e.target.value),
		};

		if (resAccount.data) {
			setFromAccount(resAccount.data[0]);
			if (designatedAccount) {
				setDesignatedAccount(null);
			}

			const accountCurrency = resAccount.data[0].currency;

			// set currency based on fromAccount
			valuesNew.currency = accountCurrency;

			// set exchange rates based on fromAccount currency and designated account currency
			const currenciesForExchangeRates = getCurrenciesForExchangeRates({
				currency: accountCurrency,
				currenciesExtra: designatedAccount?.currency ? [designatedAccount.currency] : [],
			});

			const exchangeRatesNew: ExchangeRateByDate[] = currenciesForExchangeRates.map((currency) => ({
				currency,
				dateExchangeRate: '',
				exchangeRate: 0,
			}));

			setExchangeRates(exchangeRatesNew);
		}

		// set new values
		setValues((valuesOld) => {
			return {
				...valuesOld,
				...valuesNew,
			};
		});
	};

	const handleChangeDesignatedAccount = (e: SelectChangeEvent<unknown>) => {
		const account = (fromAccount?.accounts as Account[]).find(
			(account) => String(account.id) === e.target.value
		);

		if (account) {
			const currenciesForExchangeRates = getCurrenciesForExchangeRates({
				currency: values.currency || '',
				currenciesExtra: [account.currency],
			});

			const exchangeRatesNew: ExchangeRateByDate[] = currenciesForExchangeRates.map((currency) => ({
				currency,
				dateExchangeRate: '',
				exchangeRate: 0,
			}));

			setDesignatedAccount(account);
			setExchangeRates(exchangeRatesNew);
		}

		setValues((prevState) => {
			return {
				...prevState,
				designatedAccount: String(e.target.value),
			};
		});
	};

	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = {
			...exchangeRatesNew[index],
			[name]: value,
		};
		setExchangeRates(exchangeRatesNew);
	};

	const handleChangeDate = async (e: ChangeEvent<HTMLInputElement>) => {
		// reset ifrs
		setIfrs([]);

		const reqIfrs = await fetch(
			`${process.env.REACT_APP_API_URL}/ifrs/reports?project=${project?.id}&date=${formatDate({
				date: new Date(e.target.value),
				format: 'YYYY-MM-DD',
			})}&status=!DELETED&fields=id,name`,
			{
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			}
		);

		const resIfrs = await reqIfrs.json();

		if (resIfrs.data?.length) {
			setIfrs(resIfrs.data);
		}

		setValues((prevValues) => {
			return {
				...prevValues,
				date: e.target.value,
			};
		});
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;

		const valuesNew = {
			...values,
			[name]: value,
		};

		// reset contract and toEntity if acts changes
		if (name === 'acts') {
			valuesNew.contract = '';
			valuesNew.invoice = '';
			valuesNew.toEntity = '';
		}

		// reset contract and invoice if toEntity changes
		if (name === 'toEntity') {
			valuesNew.contract = '';
			valuesNew.invoice = '';
		}

		// reset invoice if contract changes
		if (name === 'contract' && !isDirectInitiated) {
			valuesNew.invoice = '';
		}

		setValues(valuesNew);
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${formId}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const documents: FilePayload[] = [];

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		// define documents using files
		if (files.length) {
			for (let i = 0; i < files.length; i++) {
				// add document
				documents.push({
					file: files[i],
					type: entries[`file-${i}-doc-type`] ? String(entries[`file-${i}-doc-type`]) : null,
				});
			}
		}

		// define payments using loans
		const payments: PaymentPayload[] = [];

		let total = 0;

		for (let s = 0; s < loansForPayments.length; s++) {
			const loan = loansForPayments[s] as Loan;
			const amount = Number(entries[`source-${s}-amount`]) || 0;
			const tax = Number(entries[`source-${s}-tax`]) || null;

			// define conversions using exchange rates
			const conversions = exchangeRates.map((exchangeRate, indexExchangeRate) => {
				const conversionAmount =
					Number(entries[`source-${s}-amount-conversion-${indexExchangeRate}`]) || 0;
				const conversionTax =
					Number(entries[`source-${s}-tax-conversion-${indexExchangeRate}`]) || 0;

				return {
					amount: conversionAmount,
					currency: exchangeRate.currency,
					dateExchangeRate: exchangeRate.dateExchangeRate,
					exchangeRate: exchangeRate.exchangeRate,
					tax: conversionTax,
				};
			});

			total += amount;

			if (amount > 0) {
				payments.push({
					amount,
					conversions: conversions.length > 0 ? conversions : undefined,
					currency: values.currency,
					loan: loan.id,
					tax,
				});
			}
		}

		const metadata: TransactionMetadata = {
			uuid: crypto.randomUUID(),
		};

		if (hasInvoiceMetadata) {
			if (values.invoiceAmount) {
				metadata.invoiceAmount = Number(values.invoiceAmount);
			}

			if (values.invoiceCurrency) {
				metadata.invoiceCurrency = values.invoiceCurrency as string;
			}
		}

		if (values.vendor) {
			metadata.vendor = values.vendor as string;
		}

		try {
			// clear feedback
			setFeedback(null);
			// set status
			setStatus('LOADING');

			if (!total || total === 0) {
				throw new Error('The payment amount needs to be greater than 0.', {
					cause: {
						id: 'TRANSACTION_AMOUNT_NON_EXISTENT',
					},
				});
			}

			if ((isPayment || isCashBenefit) && hasRequiredDocs && documents.length === 0) {
				throw new Error('At least one document needs to be uploaded', {
					cause: {
						id: 'TRANSACTION_DOCUMENTS_NON_EXISTENT',
					},
				});
			}

			// The selected expense type must have at least one required document type configured.
			if (docTypesRequired.length === 0) {
				throw new Error(
					`The selected expense type "${expenseTypeSelected?.name}" must have at least one required document type.`,
					{
						cause: {
							id: 'EXPENSE_TYPE_REQUIRED_DOCUMENT_MISSING',
						},
					}
				);
			}

			let tags: number[] = [];

			if (values.tags.length && project) {
				// post or get tags
				const resTags = await postOrGetTags({
					names: values.tags,
					project: project.id,
				});

				if (resTags.length) {
					// define tags ids
					tags = resTags.map((tag) => tag.id);
				}
			}

			let status = 'PENDING';

			if (!hasRequiredDocs) {
				status = 'COMPLETED';
			}

			if (paramDialogStatus) {
				status = paramDialogStatus;
			}

			const categoryId =
				values.category && typeof values.category === 'object'
					? Number(values.category.id)
					: values.category
						? Number(values.category)
						: null;
			const componentId =
				values.component && typeof values.component === 'object'
					? Number(values.component.id)
					: values.component
						? Number(values.component)
						: null;
			const contractId = values.contract ? Number(values.contract) : null;
			const designatedAccountId = values.designatedAccount
				? Number(values.designatedAccount)
				: null;
			const expenseTypeId =
				values.expenseType && typeof values.expenseType === 'object'
					? Number(values.expenseType.id)
					: values.expenseType
						? Number(values.expenseType)
						: null;
			const fromAccountId = values.fromAccount ? Number(values.fromAccount) : undefined;
			const fromEntityId = values.fromEntity ? Number(values.fromEntity) : undefined;
			const invoiceId = values.invoice ? Number(values.invoice) : null;

			const body: TransactionPayload = {
				acts: values.acts.map((id) => Number(id)),
				category: categoryId,
				component: componentId,
				contract: contractId,
				date: formatDate({ date: values.date, format: 'YYYY-MM-DD' }),
				designatedAccount: designatedAccountId,
				expenseType: expenseTypeId,
				fromAccount: fromAccountId,
				fromEntity: fromEntityId,
				invoice: invoiceId,
				...(paramIsOnChain !== null && { isOnChain: isOnChainFlag }),
				payments,
				metadata,
				project: project?.id,
				status,
				tags,
				toEntity:
					Number(
						idSupplier ||
							idBeneficiary ||
							paramDialogBeneficiaryId ||
							paramDialogToEntity ||
							values.toEntity
					) || null,
				type: values.isDirect ? 'DIRECT' : type,
			};

			// add invoice documents if any
			if (hasInvoiceDocsIncluded && documentsInvoice.length) {
				body.documents = documentsInvoice.map((doc) => doc.id);
			}

			const fetchAddTransaction = await fetch(`${process.env.REACT_APP_API_URL}/transactions`, {
				method: 'post',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resAddTransaction = await fetchAddTransaction.json();

			if (resAddTransaction.error) {
				throw new Error(resAddTransaction.error.message, {
					cause: {
						details: resAddTransaction.error.details,
						id: resAddTransaction.error.id,
					},
				});
			}

			const transaction = resAddTransaction.data;

			// post comments
			if (values.comments) {
				const fetchPostComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
					method: 'post',
					body: JSON.stringify({
						comment: values.comments,
						transaction: transaction.id,
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostComment = await fetchPostComment.json();

				if (resPostComment.error) {
					feedbackNew.items.push({
						alert: {
							children: translateError({
								cause: {
									...resPostComment.error,
								},
								t,
							}),
							severity: 'error',
						},
						response: resPostComment,
					});
				}
			}

			// for each document
			for (let i = 0; i < documents.length; i++) {
				// define doc
				const doc = documents[i];

				// define form data
				const formData = new FormData();

				const pathType = isOpEx
					? 'opex'
					: `payments/${toKebabCase(String(expenseTypeSelected?.name))}`;
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/transactions/${pathType}/${transaction.id}/${year}/${month}/`
				);
				formData.append('file', doc.file);
				formData.append('transaction', String(transaction.id));

				if (values.contract) {
					formData.append('contract', String(values.contract));
				}

				if (project) {
					formData.append('project', String(project.id));
				}

				if (doc.type) {
					formData.append('type', doc.type);
				}

				// POST document
				let fetchPostDoc: Response;

				try {
					fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
						method: 'post',
						body: formData,
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});
				} catch {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({
						alert: {
							severity: 'error',
						},
						response: resPostDoc,
					});
				}
			}

			if (isDirectInitiated && values.borrowerReference && transaction.id) {
				const fetchPostWithdrawalApplication = await fetch(
					`${process.env.REACT_APP_API_URL}/withdrawal-applications`,
					{
						method: 'post',
						body: JSON.stringify({
							borrowerReference: values.borrowerReference,
							contracts: values.contract ? [Number(values.contract)] : [],
							isInitiating: true,
							project: project?.id,
							transaction: transaction.id,
						}),
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					}
				);
				const resPostWithdrawalApplication = await fetchPostWithdrawalApplication.json();

				if (resPostWithdrawalApplication.error) {
					let errorId = resPostWithdrawalApplication.error.id;

					if (!errorId || errorId === 'UNKNOWN') {
						errorId = 'WITHDRAWAL_APPLICATION_ERROR';
					}

					feedbackNew.items.push({
						alert: {
							children: translateError({
								cause: {
									...resPostWithdrawalApplication.error,
									id: errorId,
								},
								t,
							}),
							severity: 'error',
						},
						response: resPostWithdrawalApplication,
					});
				} else if (resPostWithdrawalApplication.data) {
					setWithdrawalApplication(resPostWithdrawalApplication.data as WithdrawalApplication);
				}
			}

			// set transaction
			setTransaction(transaction);

			let message = t('alert.paymentRecorded');

			if (isCashBenefit) {
				message = t('alert.cashBenefitRecorded');
			}

			if (isDirectInitiated) {
				message = t('alert.directPaymentInitiated');
			}

			if (isOpEx) {
				message = t('alert.opExRecorded');
			}

			feedbackNew.items.push({
				alert: {
					children: message,
					severity: 'success',
				},
			});

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				if (hasBanner) {
					// set success banner
					setBanner({
						hasClose: true,
						message,
						severity: 'success',
					});
				}

				if (onSuccess || onClose) {
					if (onSuccess) {
						onSuccess();
					}

					if (onClose) {
						onClose();
					}
				} else {
					// set status
					setStatus('SUCCESS');
				}
			} else {
				setFeedback(feedbackNew);
				setStatus('ERROR');
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			if (cause) {
				if (cause.id === 'TRANSACTION_EXCEEDS_CONTRACT') {
					cause.details.amountContractInInvoiceCurrency = amountContractInFromAccountCurrency;
					cause.details.transactionAmount = formatCurrency({
						currency: values.currency,
						value: amountTotal,
					});
				}

				if (cause.id === 'TRANSACTION_EXCEEDS_LOAN_AMOUNT') {
					cause.details.loanAmount = formatCurrency({
						currency: cause.details?.currency,
						value: cause.details?.loan?.amount,
					});

					cause.details.disbursedAmount = formatCurrency({
						currency: cause.details?.currency,
						value: cause.details?.totalDisbursed,
					});
				}
			}

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});

			setFeedback(feedbackNew);
			setStatus('ERROR');
		}
	};

	const docTypesChecked = [...new Set(docTypesSelected)];

	const renderAmounts = () => {
		const AmountsProps: Partial<AmountsProps> = {
			balances: [],
			hasCurrency: false,
			loans: [],
		};

		let hasAmountsFields = false;

		if (values.isDirect) {
			hasAmountsFields = true;
			AmountsProps.hasCurrency = true;
			AmountsProps.loans = loans;
		} else if (fromAccount) {
			AmountsProps.balances = fromAccount.balances;
			AmountsProps.loans = fromAccount.loans as Loan[];

			if (
				(fromAccount.loans.length && !hasDesignatedAccount) ||
				(hasDesignatedAccount && values.designatedAccount)
			) {
				hasAmountsFields = true;
			}
		}

		if (hasAmountsFields && !isLoadingLoans) {
			if (AmountsProps.loans?.length) {
				return (
					<>
						<Grid item xs={12}>
							<Amounts
								currency={values.currency}
								exchangeRates={exchangeRates}
								formId={formId}
								hasTax={true}
								onChange={(e, total, loan) => {
									const amountsNew: ValuesProps['amounts'] = [...values.amounts];
									const isTax = e.target.name.includes('tax');

									// find amount by loan
									const amountByLoan = amountsNew.find((a) => a.loan === loan.id);
									const amount = Number(e.target.value);

									if (amountByLoan) {
										// update existing amount
										if (isTax) {
											amountByLoan.tax = amount;
										} else {
											amountByLoan.amount = amount;
										}
									} else {
										// add new amount
										amountsNew.push({
											loan: loan.id,
											amount: isTax ? 0 : amount,
											tax: isTax ? amount : 0,
										});
									}

									setValues((prevState) => {
										return {
											...prevState,
											amounts: amountsNew,
											total,
										};
									});
								}}
								onChangeCurrency={(e) => {
									const currencyNew = e.target.value as string;

									// Update exchange rates for new currency
									const exchangeRatesNew = getExchangeRatesForCurrency(currencyNew);
									setExchangeRates(exchangeRatesNew);

									// set currency and reset amounts
									setValues((prevState) => {
										return {
											...prevState,
											amounts: [],
											currency: currencyNew,
										};
									});
								}}
								title={t('financingSources')}
								{...AmountsProps}
							/>
						</Grid>
						{hasDisabledLoans && (
							<Grid item xs={12}>
								<Alert severity="info">{t('alert.financingSourcesDisabled')}</Alert>
							</Grid>
						)}
					</>
				);
			} else {
				return (
					<Grid item xs={12}>
						<Alert severity="info">{t('alert.accountNoFinancingSources')}</Alert>
					</Grid>
				);
			}
		}

		return <></>;
	};

	// fetch category or component to get loan cash out data
	const fetchCategoryOrComponent = useCallback(
		async ({ id, route }: { id?: number; route?: 'categories' | 'components' }) => {
			if (!accessToken || !fromAccount || !idToken || !project || (!id && !route)) {
				setLoansCashOut([]);
				return;
			}

			if (!id || !route) {
				setLoansCashOut([]);
				return;
			}

			try {
				const reqCategoryOrComponent = await fetch(
					`${process.env.REACT_APP_API_URL}/${route}/${id}?project=${project?.id}&fields=allocations,cashOut&cashOutCurrency=${values.currency}&populate=allocations,allocations.conversions,cashOut`,
					{
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					}
				);

				if (!reqCategoryOrComponent.ok) {
					throw new Error(`Failed to fetch ${route}`);
				}

				const resCategory = await reqCategoryOrComponent.json();
				const resource =
					route === 'categories'
						? (resCategory.data[0] as Category)
						: (resCategory.data[0] as Component);

				const allocations: CategoryAllocation[] | ComponentAllocation[] =
					route === 'categories'
						? (resource as Category).allocations || []
						: (resource as Component).allocations || [];

				if (allocations.length === 0) {
					setLoansCashOut([]);
					return;
				}

				const cashOutTotals = (resource as Category | Component).cashOut || [];
				const loansCashOutNew: LoanCashOut[] = [];

				for (const allocation of allocations) {
					// get allocation in fromAccount currency
					const allocationInCurrency = getAllocation({
						allocations,
						currency: values.currency,
						loanId: allocation.loan,
					});

					if (!allocationInCurrency) {
						continue;
					}

					const allocationAmount = allocationInCurrency.amount;

					const cashOutForLoan = cashOutTotals.find(
						(c: CashOutTotal) => c.loan === allocation.loan && c.currency === values.currency
					);
					const cashOutAmount = cashOutForLoan ? cashOutForLoan.total : 0;

					loansCashOutNew.push({
						...(route === 'categories' ? { category: resource.id } : { component: resource.id }),
						allocation: allocationAmount,
						cashOut: cashOutAmount,
						currency: values.currency,
						loan: allocation.loan,
						undisbursed: Number((allocationAmount - cashOutAmount).toFixed(2)),
					});
				}

				setLoansCashOut(loansCashOutNew);
			} catch (error) {
				console.error('Error fetching undisbursed data:', error);
				setLoansCashOut([]);
			}
		},
		[accessToken, fromAccount, idToken, project, values.currency]
	);

	// set feedback when contract or category changes
	useEffect(() => {
		setFeedback((feedbackPrev) => {
			// filter out previous feedback items to prevent duplicates
			const feedbackItemsPrev = (feedbackPrev?.items || []).filter(
				(item) =>
					item.type !== 'FIELD' ||
					![
						'TRANSACTION_CONTRACT_STEP_ID_NON_EXISTENT',
						'TRANSACTION_CATEGORY_ID_NON_EXISTENT',
					].includes(String(item.id))
			);
			const feedbackItemsNext = [...feedbackItemsPrev];

			if (isDirectInitiated) {
				if (contract && !contract?.idStep) {
					feedbackItemsNext.push({
						alert: {
							action: (
								<Button
									component={RouterLink}
									size="small"
									to={`/project/${project?.idWbg}/contracts/details?dialog=editContract&dialogContract=${contract.id}`}
									variant="contained">
									{t('updateContract')}
								</Button>
							),
							children: t('alert.contractStepIdRequired'),
							severity: 'error',
						},
						type: 'FIELD',
						id: 'TRANSACTION_CONTRACT_STEP_ID_NON_EXISTENT',
					});
				}

				if (categorySelected && !categorySelected?.idWbg) {
					feedbackItemsNext.push({
						alert: {
							action: (
								<Button
									component={RouterLink}
									size="small"
									to={`/project/${project?.idWbg}/settings/categories?dialog=editCategory&dialogCategory=${categorySelected?.id}`}
									variant="contained">
									{t('updateCategory')}
								</Button>
							),
							children: t('alert.categoryCcCategroyRequired'),
							severity: 'error',
						},
						type: 'FIELD',
						id: 'TRANSACTION_CATEGORY_ID_NON_EXISTENT',
					});
				}
			}

			if (!feedbackItemsNext.length && !feedbackPrev?.items?.length) {
				return null;
			}

			return {
				...(feedbackPrev || {}),
				items: feedbackItemsNext,
			};
		});
	}, [categorySelected, contract, isDirectInitiated, project?.idWbg, t]);

	// set toEntity if idSupplier or paramDialogToEntity exist
	useEffect(() => {
		const id = idSupplier || paramDialogToEntity;

		if (type === 'PAYMENT' && id) {
			setValues((valuesPrev) => {
				return {
					...valuesPrev,
					toEntity: id,
				};
			});
		}
	}, [categorySelected, idSupplier, paramDialogToEntity, type]);

	// fetch suppliers when acts changes
	useEffect(() => {
		if (isDirectOrPayment) {
			fetchSuppliers();
		}
	}, [fetchSuppliers, isDirectOrPayment, type, values.acts]);

	// fetch contracts when toEntity changes
	useEffect(() => {
		if (isDirectOrPayment && values.toEntity) {
			fetchContracts();
		}
	}, [fetchContracts, isDirectOrPayment, type, values.toEntity]);

	// fetch invoices when contract changes (only when !hasInvoiceMetadata)
	useEffect(() => {
		if (!hasInvoiceMetadata && isDirectOrPayment && values.contract) {
			fetchInvoices();
		} else if (!isDirectInitiated) {
			setInvoices([]);
		}
	}, [fetchInvoices, hasInvoiceMetadata, isDirectInitiated, isDirectOrPayment, values.contract]);

	// fetch invoice documents when invoice changes
	useEffect(() => {
		if (values.invoice) {
			fetchInvoiceDocuments();
		} else {
			setDocumentsInvoice([]);
		}
	}, [fetchInvoiceDocuments, values.invoice]);

	// set classification values from selected contract
	useEffect(() => {
		if (!contract) {
			return;
		}

		setValues((valuesPrev) => {
			const valuesNew = { ...valuesPrev };
			const categoryValue = categories.find((c) => c.id === contract.categories?.[0]);
			const componentValue = components.find((c) => c.id === contract.components?.[0]);
			const expenseTypeValue = expenseTypes.find((e) => e.id === contract.expenseTypes?.[0]);

			if (categoryValue) {
				valuesNew.category = categoryValue;
			}

			if (componentValue) {
				valuesNew.component = componentValue;
			}

			if (expenseTypeValue) {
				valuesNew.expenseType = expenseTypeValue;
			}

			return valuesNew;
		});
	}, [categories, components, contract, expenseTypes]);

	// fetch category data when category changes
	useEffect(() => {
		if (values.category) {
			fetchCategoryOrComponent({ id: (values.category as Category).id, route: 'categories' });
		}
	}, [fetchCategoryOrComponent, values.category]);

	// fetch component data when component changes
	useEffect(() => {
		if (values.component) {
			fetchCategoryOrComponent({ id: (values.component as Component).id, route: 'components' });
		}
	}, [fetchCategoryOrComponent, values.component]);

	// set exchange rates when currency changes
	useEffect(() => {
		const exchangeRatesNew = getExchangeRatesForCurrency(values.currency || '');
		setExchangeRates(exchangeRatesNew);
	}, [getExchangeRatesForCurrency, values.currency]);

	// reset fields when isDirect changes
	useEffect(() => {
		setExchangeRates([]);
		setFromAccount(null);
		setValues((prevState) => {
			return {
				...prevState,
				amounts: [],
				designatedAccount: '',
				fromAccount: '',
				total: 0,
			};
		});
	}, [values.isDirect]);

	// set feedback to parent component
	useEffect(() => {
		if (onFeedback) {
			onFeedback(feedback);
		}
	}, [feedback, onFeedback]);

	// loading and success states
	if (['LOADING', 'SUCCESS'].includes(status)) {
		const content = {
			children: t('empty.addTransaction.loading.content'),
		};

		const IconCircleProps: IconCircleProps = {
			size: 'xl',
			type: 'cube',
		};

		const sxBox: SxProps = {
			marginTop: theme.spacing(4),
			marginBottom: theme.spacing(4),
		};

		const title = {
			children: t('empty.addTransaction.loading.title'),
		};

		if (isDirectInitiated) {
			content.children = t('empty.addTransaction.directInitiated.loading.content');
			title.children = t('empty.addTransaction.directInitiated.loading.title');
		}

		if (status === 'SUCCESS') {
			content.children = t('empty.addTransaction.success.content');
			title.children = t('empty.addTransaction.success.title');
			IconCircleProps.type = 'check';
			sxBox.marginTop = 0;
			sxBox.marginBottom = 0;

			if (isDirectInitiated) {
				content.children = t('empty.addTransaction.directInitiated.success.content');
				title.children = t('empty.addTransaction.directInitiated.success.title');
			}
		}

		return (
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Empty content={content} IconCircleProps={IconCircleProps} sxBox={sxBox} title={title} />
				</Grid>
				{status === 'SUCCESS' && (
					<Grid item xs={12}>
						<TableInfoTransaction
							acts={acts.filter((act) => values.acts.includes(String(act.id)))}
							category={values.category as Category}
							component={values.component as Component}
							contract={contract}
							date={values.date}
							designatedAccount={designatedAccount}
							expenseType={values.expenseType as ExpenseType}
							fromAccount={fromAccount as Account}
							fromEntity={fromEntitySelected}
							invoice={invoices.find((inv) => String(inv.id) === values.invoice)}
							payments={
								transaction?.payments
									? transaction.payments.map((payment) => {
											return {
												...payment,
												loan: loansForPayments.find((loan) => loan.id === payment.loan) || null,
											};
										})
									: []
							}
							toEntity={toEntitySelected}
							withdrawalApplication={withdrawalApplication}
							total={{
								amount: values.total,
								currency: values.currency,
							}}
							type={type}
						/>
					</Grid>
				)}
			</Grid>
		);
	}

	const warnings: string[] = [];

	// add warning if no contract is selected for payments
	if (isPayment && !values.contract) {
		warnings.push(t('warning.selectContract'));
	}

	// add warning if contract currency is different from fromAccount currency
	if (isContractCurrencyDifferent) {
		warnings.push(
			t('warning.contractCurrencyDifferent', {
				contractCurrency: contract?.amounts.map((a) => a.currency).join(', ') || t('na'),
				fromAccountCurrency: values.currency,
			})
		);
	}

	// add warning if invoice amount exceeds contract amount
	if (isInvoiceGreaterThanContract) {
		warnings.push(
			t('warning.invoiceExceedsContract', {
				contractAmount: amountContractInInvoiceCurrency,
				invoiceAmount: formatCurrency({
					currency: invoiceCurrency,
					value: Number(invoiceAmount),
				}),
			})
		);
	}

	// add warning if amount exceeds invoice amount
	if (isAmountGreaterThanInvoice && fromAccount) {
		warnings.push(
			t('warning.transactionExceedsInvoice', {
				transactionAmount: formatCurrency({
					currency: values.currency,
					value: amountTotal,
				}),
				invoiceAmount: formatCurrency({
					currency: invoiceCurrency,
					value: Number(invoiceAmount),
				}),
			})
		);
	}

	// add warnings for loans exceeding undisbursed amounts
	if (fromAccount && loansCashOut.length > 0) {
		for (const loanCashOut of loansCashOut) {
			const amountForLoan = values.amounts.find((a) => a.loan === loanCashOut.loan)?.amount || 0;

			if (amountForLoan > 0 && amountForLoan > loanCashOut.undisbursed) {
				const loan = fromAccountLoans.find((l) => l.id === loanCashOut.loan);

				if (loan) {
					let resource = '';

					if (loanCashOut.category) {
						resource = (values.category as Category).name;
					}

					if (loanCashOut.component) {
						resource = (values.component as Component).name;
					}

					warnings.push(
						t('warning.transactionExceedsLoanAmount', {
							loan,
							resource,
							transactionAmount: formatCurrency({
								currency: values.currency,
								value: amountForLoan,
							}),
							undisbursedAmount: formatCurrency({
								currency: values.currency,
								value: loanCashOut.undisbursed,
							}),
						})
					);
				}
			}
		}
	}

	let helperTextInvoice: string | undefined;

	if (!values.contract) {
		helperTextInvoice = t('formAddTransaction.payment.invoice.contractHelper');
	} else if (!optionsInvoices.length) {
		helperTextInvoice = t('formAddTransaction.payment.invoice.helper');
	}
	if (invoiceSelected?.amount && invoiceSelected?.currency) {
		helperTextInvoice = formatCurrency({
			currency: invoiceSelected.currency,
			value: invoiceSelected.amount,
		});
	} else {
		helperTextInvoice = undefined;
	}

	const feedbackItemsFiltered = (feedback?.items || []).filter((item) => item.type !== 'FIELD');
	const feedbackItemsField = (feedback?.items || []).filter((item) => item.type === 'FIELD');
	const feedbackItemContractStepId = feedbackItemsField.find(
		(item) => item.id === 'TRANSACTION_CONTRACT_STEP_ID_NON_EXISTENT'
	);
	const feedbackItemCategoryId = feedbackItemsField.find(
		(item) => item.id === 'TRANSACTION_CATEGORY_ID_NON_EXISTENT'
	);

	const filesFromInvoiceDocs = documentsInvoice.map((doc) => {
		return {
			document: doc,
			file: new File([doc.blob], doc.name, {
				type: doc?.metadata?.type,
			}),
		};
	});

	return (
		<form id={formId} name="formAddTransaction" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{featureFlags['DIRECT'] && isDirectOrPayment && isWfa && (
					<Grid item xs={12}>
						<Checkbox
							id={`${formId}-is-direct`}
							checked={values.isDirect}
							disabled={isDirectInitiated}
							label={t('directPayment')}
							name="isDirect"
							onChange={() => {
								setValues((valuesPrev) => {
									return {
										...valuesPrev,
										isDirect: !valuesPrev.isDirect,
									};
								});
							}}
							required={isDirectInitiated}
						/>
					</Grid>
				)}
				{isPayment && featureFlags['ACTIVITIES'] && (
					<Grid item xs={12}>
						<Select
							disabled={!optionsActs.length}
							helperText={
								!optionsActs.length ? t('formAddTransaction.payment.acts.helper') : undefined
							}
							label={t('activities')}
							multiple={true}
							name="acts"
							onChange={handleChangeValue}
							options={optionsActs}
							value={values.acts}
						/>
					</Grid>
				)}
				{isDirectOrPayment && !idSupplier && (
					<Grid item xs={6}>
						<Select
							disabled={!optionsSuppliers.length || Boolean(paramDialogToEntity)}
							id={`${formId}-to-entity`}
							label={t('supplier')}
							name="toEntity"
							onChange={handleChangeValue}
							options={optionsSuppliers}
							required={true}
							value={values.toEntity}
						/>
					</Grid>
				)}
				{isDirectOrPayment && (
					<Grid item xs={idSupplier ? 12 : 6}>
						<Select
							disabled={!optionsContracts.length}
							helperText={
								!optionsContracts.length
									? t('formAddTransaction.payment.contract.helper')
									: undefined
							}
							id={`${formId}-contract`}
							label={isDirectInitiated ? t('priorReviewContract') : t('contract')}
							name="contract"
							options={optionsContracts}
							onChange={handleChangeValue}
							value={values.contract}
						/>
					</Grid>
				)}
				{isDirectInitiated && contract?.idStep && (
					<Grid item xs={12}>
						<Input
							disabled={true}
							id={`${formId}-step-id`}
							label={t('stepId')}
							name="idStep"
							required={true}
							value={contract?.idStep}></Input>
					</Grid>
				)}
				{feedbackItemContractStepId && (
					<Grid item xs={12}>
						<Alert {...feedbackItemContractStepId.alert} />
					</Grid>
				)}
				{!isLoadingActs && values.acts.length > 0 && !optionsSuppliers.length && (
					<Grid item xs={12}>
						<Alert severity="info">{t('alert.noActsSuppliers')}</Alert>
					</Grid>
				)}
				{isPayment && hasInvoiceMetadata && (
					<>
						<Grid item xs={12} md={6}>
							<Input
								id={`${formId}-invoice-amount`}
								label={t('invoiceAmount')}
								name="invoiceAmount"
								onChange={handleChangeValue}
								type="number"
								value={values.invoiceAmount}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<SelectCurrency
								id={`${formId}-invoice-currency`}
								label={t('invoiceCurrency')}
								name="invoiceCurrency"
								onChange={handleChangeValue}
								value={values.invoiceCurrency}
							/>
						</Grid>
					</>
				)}
				{isDirectOrPayment && !hasInvoiceMetadata && (
					<Grid item xs={12}>
						<Select
							disabled={!values.contract || !optionsInvoices.length || Boolean(paramDialogInvoice)}
							helperText={helperTextInvoice}
							id={`${formId}-invoice`}
							label={t('invoice')}
							name="invoice"
							onChange={handleChangeValue}
							options={optionsInvoices}
							required={isDirectInitiated}
							value={values.invoice}
						/>
					</Grid>
				)}
				{isDirectInitiated && (
					<Grid item xs={12}>
						<Input
							id={`${formId}-borrower-reference`}
							label={t('borrowerReference#')}
							name="borrowerReference"
							onChange={handleChangeValue}
							required={true}
							value={values.borrowerReference}
						/>
					</Grid>
				)}
				{isCashBenefit && !idBeneficiary && !paramDialogBeneficiaryId && (
					<Grid item xs={12}>
						<Select
							id={`${formId}-to-entity`}
							label={t('beneficiary')}
							name="toEntity"
							onChange={handleChangeValue}
							options={optionsBeneficiaries}
							required={true}
							value={values.toEntity}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={6}>
					<Select
						id={`${formId}-component`}
						label={t('component')}
						name="component"
						onChange={(e) => {
							setValues((valuesPrev) => {
								const valuesNew = { ...valuesPrev };
								const component = components.find((c) => c.id === Number(e.target.value));

								if (component) {
									valuesNew.component = component;
								}

								return valuesNew;
							});
						}}
						options={optionsComponents}
						required={true}
						value={values.component ? String((values.component as Component).id) : ''}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${formId}-category`}
						label={t('category')}
						name="category"
						onChange={(e) => {
							setValues((valuesPrev) => {
								const valuesNew = { ...valuesPrev };
								const category = categories.find((c) => c.id === Number(e.target.value));

								if (category) {
									valuesNew.category = category;
								}

								return valuesNew;
							});
						}}
						options={optionsCategories}
						required={true}
						value={values.category ? String((values.category as Category).id) : ''}
					/>
				</Grid>
				{feedbackItemCategoryId && (
					<Grid item xs={12}>
						<Alert {...feedbackItemCategoryId.alert} />
					</Grid>
				)}
				<Grid item xs={12} md={6}>
					<Select
						id={`${formId}-expense-type`}
						label={t('expenseType')}
						name="expenseType"
						onChange={(e, child) => {
							setValues((prevState) => {
								return {
									...prevState,
									expenseType: (child as ReactElement).props.resource as ExpenseType,
								};
							});
							setDocTypesSelected([]);
						}}
						options={optionsExpenseTypes}
						required={true}
						value={values.expenseType ? String((values.expenseType as ExpenseType).id) : ''}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Input
						id={`${formId}-date`}
						inputProps={{
							max: new Date().toISOString().split('T')[0],
						}}
						label={t('transactionDate')}
						name="date"
						onChange={handleChangeDate}
						required={true}
						type="date"
						value={values.date}
					/>
				</Grid>
				{isDateAfterConfirmation && dateProjectConfirmation && (
					<Grid item xs={12}>
						<Alert severity="info">
							{isPayment
								? t('alert.transactionPaymentDateAfterConfirmation', {
										date: dateProjectConfirmation,
									})
								: t('alert.transactionCashBenefitDateAfterConfirmation', {
										date: dateProjectConfirmation,
									})}
						</Alert>
					</Grid>
				)}
				{ifrs.length > 0 && (
					<Grid item xs={12}>
						<Alert severity="info">
							{t('alert.transactionIncludedInIfrs')}
							<ul>
								{ifrs.map((report) => (
									<li key={report.id}>
										<strong>{report.name}</strong>
									</li>
								))}
							</ul>
						</Alert>
					</Grid>
				)}
				{isOpEx && (
					<>
						<Grid item xs={12}>
							<Input
								id={`${formId}-vendor`}
								label={t('vendor')}
								name="vendor"
								onChange={handleChangeValue}
								value={values.vendor}
							/>
						</Grid>
						{!idEntity && (
							<Grid item xs={12} lg={6}>
								<Select
									helperText={
										values.fromAccount === ''
											? t('formAddTransaction.opex.sendingAccount.helper')
											: undefined
									}
									id={`${formId}-from-entity`}
									label={t('entity')}
									name="fromEntity"
									onChange={handleChangeEntity}
									options={optionsEntities}
									required={true}
									value={values.fromEntity}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={idEntity ? 12 : 6}>
							<Select
								disabled={!values.fromEntity || !optionsAccounts.length}
								id={`${formId}-account`}
								label={t('account')}
								name="fromAccount"
								onChange={handleChangeFromAccount}
								options={optionsAccounts}
								required={true}
								value={values.fromAccount}
							/>
						</Grid>
					</>
				)}
				<>
					{!isOpEx && !values.isDirect && (
						<Grid item xs={12} lg={12}>
							<Select
								helperText={
									values.fromAccount === ''
										? t('formAddTransaction.payment.sendingAccount.helper')
										: undefined
								}
								id={`${formId}-account`}
								label={t('sendingAccount')}
								name="fromAccount"
								onChange={handleChangeFromAccount}
								options={optionsAccounts}
								required={true}
								value={values.fromAccount}
							/>
						</Grid>
					)}
				</>
				{hasDesignatedAccount && (
					<Grid item xs={12} lg={12}>
						<Select
							helperText={t('formAddTransaction.designatedAccount.helper')}
							id={`${formId}-designated-account`}
							label={t('designatedAccount')}
							name="designatedAccount"
							onChange={handleChangeDesignatedAccount}
							options={optionsAccountsDesignated}
							required={true}
							value={values.designatedAccount}
						/>
					</Grid>
				)}
				{exchangeRates.length > 0 && values.currency && (
					<Grid item xs={12}>
						<ExchangeRates
							currencyBase={values.currency}
							exchangeRates={exchangeRates}
							onChange={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				{renderAmounts()}
				<Grid item xs={12}>
					<FilePicker
						filesStatic={{
							files: filesFromInvoiceDocs,
							footer: {
								children: t('formAddTransaction.files.static.footer'),
							},
						}}
						onChange={(e, filesSelected) => {
							if (optionsDocTypes.length) {
								const defaultDocType = String(optionsDocTypes[0].value);

								if (!docTypesSelected.includes(defaultDocType)) {
									setDocTypesSelected([...docTypesSelected, defaultDocType]);
								}
							}

							setFiles(filesSelected);
						}}
						onChangeDocType={(e, i) => {
							// clone doc types selected
							const docTypesSelectedNew = [...docTypesSelected];

							// define new doc type
							const docTypeNew = String(e.target.value);

							// replace or add doc type
							if (docTypesSelectedNew[i]) {
								docTypesSelectedNew[i] = docTypeNew;
							} else {
								docTypesSelectedNew.push(docTypeNew);
							}

							setDocTypesSelected(docTypesSelectedNew);
						}}
						onRemove={(e, i) => {
							// clone doc types selected
							const docTypesSelectedNew = [...docTypesSelected];

							// remove selected doc by index
							docTypesSelectedNew.splice(i, 1);

							setDocTypesSelected(docTypesSelectedNew);
						}}
						optionsDocTypes={optionsDocTypes}
						required={hasRequiredDocs && !isDirectInitiated}
					/>
				</Grid>
				{!isOpEx && values.expenseType && (
					<Grid item xs={12}>
						<DocsTraceability
							docTypes={docTypesForExpenseTypeSelected}
							docTypesChecked={docTypesChecked}
						/>
					</Grid>
				)}
				{userProjectPermissions?.comment?.create && (
					<Grid item xs={12}>
						<Input
							id={`${formId}-comments`}
							label={t('comments')}
							minRows={2}
							multiline={true}
							name="comments"
							onChange={handleChangeValue}
							value={values.comments}
						/>
					</Grid>
				)}
				{userProjectPermissions?.tag?.create && (
					<Grid item xs={12}>
						<AutocompleteTags
							id={`${formId}-tags`}
							onChange={(tags) => {
								setValues((prevState) => ({
									...prevState,
									tags,
								}));
							}}
							value={values.tags}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<CheckboxConfirmation formId={formId} />
				</Grid>
				{warnings.length > 0 && (
					<Grid item xs={12}>
						<Alert severity="warning">
							<AlertTitle>{t('warnings')}</AlertTitle>
							<ul>
								{warnings.map((warning, index) => (
									<li key={index}>{warning}</li>
								))}
							</ul>
						</Alert>
					</Grid>
				)}
				{feedbackItemsFiltered?.length > 0 && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
